import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CameraIcon, ChevronLeftIcon, ChevronRightIcon, MapPinIcon, SparklesIcon, WrenchIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { projects } from '../data/projects';
import { Reveal } from './Reveal';
import { SectionHeading } from './SectionHeading';
const phaseMeta = {
  before: {
    icon: CameraIcon,
    labelKey: 'show.before',
    chip: 'bg-navy-900 text-white'
  },
  process: {
    icon: WrenchIcon,
    labelKey: 'show.process',
    chip: 'bg-amber-600 text-white'
  },
  after: {
    icon: SparklesIcon,
    labelKey: 'show.after',
    chip: 'bg-forest-600 text-white'
  }
} as const;
export function ProjectShowcase() {
  const {
    t
  } = useLanguage();
  const [index, setIndex] = useState(0);
  const project = projects[index];
  const go = (dir: 1 | -1) => setIndex(prev => (prev + dir + projects.length) % projects.length);
  return <section id="gallery" className="w-full bg-navy-50/60 py-20 sm:py-24" data-id="element-217" data-mp="11t320mmu1hpe">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-218" data-mp="1gzdjfdbh3d0t">
        <Reveal data-id="element-219" data-mp="1lxyaam4u4kcl">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between" data-id="element-220" data-mp="1lwdeyuap4xdo">
            <SectionHeading eyebrow={t('show.eyebrow')} title={t('show.title')} description={t('show.sub')} data-id="element-221" data-mp="177qwijprvpi8" />
            <div className="flex items-center gap-3" data-id="element-222" data-mp="y8dolvhar8zm">
              <span className="font-display text-sm font-bold text-navy-900/60" data-id="element-223" data-mp="y6j58lloboxn">
                {t('show.counter')} {index + 1} / {projects.length}
              </span>
              <div className="flex gap-2" data-id="element-224" data-mp="oazk8u8neano">
                <button type="button" onClick={() => go(-1)} aria-label={t('show.prev')} className="flex h-11 w-11 items-center justify-center rounded-md border border-navy-900/15 bg-white text-navy-900 transition-colors duration-150 ease-smooth hover:border-amber-600 hover:text-amber-600" data-id="element-225" data-mp="oc0y16h1tb93">
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-226" data-mp="2m7g0o3w54xen" />
                </button>
                <button type="button" onClick={() => go(1)} aria-label={t('show.next')} className="flex h-11 w-11 items-center justify-center rounded-md border border-navy-900/15 bg-white text-navy-900 transition-colors duration-150 ease-smooth hover:border-amber-600 hover:text-amber-600" data-id="element-227" data-mp="2h57atgekebgr">
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-228" data-mp="2zykv8rknizi4" />
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-10 overflow-hidden rounded-2xl border border-navy-900/10 bg-white shadow-card" data-id="element-229" data-mp="2r5hmgwkybatt">
          <AnimatePresence mode="wait" data-id="element-230" data-mp="3nn43x87m5eaq">
            <motion.div key={project.id} initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.28,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-231" data-mp="3no5ezcomq0sg">
              <div className="flex flex-col gap-4 border-b border-navy-900/10 p-6 sm:flex-row sm:items-start sm:justify-between sm:p-8" data-id="element-232" data-mp="1adds0mo9m19y">
                <div className="max-w-2xl" data-id="element-233" data-mp="1egadcl56wbhl">
                  <span className="inline-flex items-center rounded-full bg-brand-600/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-brand-700" data-id="element-234" data-mp="1jdiz0vpjqxnl">
                    {t(project.categoryKey)}
                  </span>
                  <h3 className="mt-3 font-display text-2xl font-extrabold text-navy-900 sm:text-3xl" data-id="element-235" data-mp="1t9vtvwwtvhjx">
                    {t(project.titleKey)}
                  </h3>
                  <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-navy-900/60" data-id="element-236" data-mp="2240dbv9mtqpf">
                    <MapPinIcon className="h-4 w-4 text-amber-600" aria-hidden="true" data-id="element-237" data-mp="273mtodkrbn2g" />
                    {t(project.locationKey)}
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-navy-900/70" data-id="element-238" data-mp="2bzlk06pnbf5i">
                    {t(project.descKey)}
                  </p>
                </div>
                <a href="#contact" className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-navy-900 px-5 py-3 text-sm font-bold text-white transition-[background-color,transform] duration-150 ease-smooth hover:bg-brand-600 active:scale-[0.98]" data-id="element-239" data-mp="1tflbpl1hag2f">
                  {t('show.cta')}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-240" data-mp="1aha6b4dlmvoo" />
                </a>
              </div>

              <ol className="grid grid-cols-1 divide-y divide-navy-900/10 md:grid-cols-3 md:divide-x md:divide-y-0" data-id="element-241" data-mp="1pf2hjkc44xx2">
                {project.phases.map(phase => {
                const meta = phaseMeta[phase.label];
                const Icon = meta.icon;
                return <li key={phase.label} className="relative" data-id="element-242" data-mp="2vuv9h1yndfug">
                      <div className="relative aspect-[4/3] w-full overflow-hidden bg-navy-100" data-id="element-243" data-mp="2vwpndwbi47p1">
                        <img src={phase.src} alt={phase.alt} loading="lazy" className="h-full w-full object-cover" data-id="element-244" data-mp="33g78vf8b37o8" />
                        <span className={`absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wide shadow-sm ${meta.chip}`} data-id="element-245" data-mp="2yehze40qf229">
                          <Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-246" data-mp="3r36xdu6hqr9s" />
                          {t(meta.labelKey)}
                        </span>
                      </div>
                    </li>;
              })}
              </ol>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2" data-id="element-247" data-mp="8w966rtioh3p">
          {projects.map((item, i) => <button key={item.id} type="button" onClick={() => setIndex(i)} aria-label={t(item.titleKey)} aria-current={i === index} className={`max-w-[220px] truncate rounded-full px-4 py-2 text-xs font-bold transition-colors duration-150 ease-smooth ${i === index ? 'bg-navy-900 text-white' : 'bg-white text-navy-900/60 ring-1 ring-navy-900/10 hover:text-navy-900'}`} data-id="element-248" data-mp="nkiwq9avjv37" data-mpi={i}>
              {t(item.titleKey)}
            </button>)}
        </div>
      </div>
    </section>;
}